import {
  LoadingContainer,
  PageLoadingIndicator,
  PageLoadingSpinner,
} from 'features/knowledgeDocumentViewer/components/knowledgeDocumentToolsStyles'
import {
  type ImageViewerRolodexOverride,
  useImageViewerLogic,
} from 'features/knowledgeDocumentViewer/components/viewer/image/hooks/useImageViewerLogic'
import {
  computePagesToPreload,
  RolodexPagePreloader,
} from 'features/knowledgeDocumentViewer/components/viewer/image/hooks/useRolodexPagePreloader'
import ImageViewerWithOverlay from 'features/knowledgeDocumentViewer/components/viewer/image/ImageViewerWithOverlay'
import type { SearchHighlightData } from 'features/knowledgeDocumentViewer/components/viewer/search/SearchHighlightOverlay'
import { DocumentMinimap } from 'features/knowledgeDocumentViewer/components/viewer/shared/DocumentMinimap'
import { useMinimapPreference } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useMinimapPreference'
import { DocumentViewerRoot } from 'features/knowledgeDocumentViewer/components/viewer/shared/styles'
import {
  type DocumentViewerHandle,
  type FrameTarget,
  type TransformState,
  type ViewerCommonProps,
} from 'features/knowledgeDocumentViewer/components/viewer/shared/types'
import { ZoomControls } from 'features/knowledgeDocumentViewer/components/viewer/shared/ZoomControls'
import { useCantileverPageHostsOptional } from 'features/knowledgeDocumentViewer/contexts/CantileverPageHostsContext'
import { DocumentViewerPage } from 'features/knowledgeDocumentViewer/types'
import { resolvePageImageFileRefDual } from 'features/knowledgeDocumentViewer/utils/documentPageImages'
import { useRolodexSignedURLs } from 'features/rolodex'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

export type PagedDocumentViewerProps = ViewerCommonProps & {
  documentId: string
  isActiveTab: boolean
  pageNumber: number
  pdfUrl: string
  scale: number
  totalPages: number
  showMultiplePages: boolean
  searchHighlights?: SearchHighlightData
  frameTarget?: FrameTarget
  onFrameApplied?: () => void
}

export const PagedDocumentViewer = forwardRef<
  DocumentViewerHandle,
  PagedDocumentViewerProps
>(function PagedDocumentViewer(
  {
    documentId,
    isActiveTab,
    pageNumber,
    pdfUrl,
    scale,
    totalPages,
    showMultiplePages,
    searchHighlights,
    frameTarget,
    onFrameApplied,
    highlightBoxIds,
    onBoxClick,
    onBoxAuxClick,
    onBoxContextMenu,
    onBoxHover,
    onMultipleBoxSelect,
    onAliasClick,
    onDocReferenceClick,
    onDocReferenceHover,
    actionMode,
    showTooltips = false,
  },
  ref
) {
  const viewerRef = useRef<DocumentViewerHandle>(null)
  const viewerRootRef = useRef<HTMLDivElement | null>(null)
  const [isAtMinZoom, setIsAtMinZoom] = useState(false)
  const [isAtMaxZoom, setIsAtMaxZoom] = useState(false)
  const [transformState, setTransformState] = useState<TransformState>({
    scale: 1,
    positionX: 0,
    positionY: 0,
  })
  const [imageNaturalDimensions, setImageNaturalDimensions] = useState({
    w: 0,
    h: 0,
  })
  const [wrapperDimensions, setWrapperDimensions] = useState({ w: 0, h: 0 })
  const scaleAtMinZoom = useRef(1)
  const currentScaleRef = useRef(1)
  const isResettingZoomRef = useRef(false)
  const latestTransformStateRef = useRef<TransformState>(transformState)
  const transformFrameRef = useRef<number | null>(null)

  const pageHosts = useCantileverPageHostsOptional()
  const leftPageHostId = pageHosts?.getCantileverPageHostId(pageNumber)
  const leftPageRolodexQuery = useMemo(
    () =>
      resolvePageImageFileRefDual(
        documentId,
        pageNumber,
        'hiRes',
        leftPageHostId
      ),
    [documentId, leftPageHostId, pageNumber]
  )
  const { url: leftPage6400Url, isLoading: isLeftPage6400Loading } =
    useRolodexSignedURLs(leftPageRolodexQuery)

  const hasSecondPage =
    showMultiplePages && totalPages > 1 && pageNumber < totalPages

  const rightPageHostId = hasSecondPage
    ? pageHosts?.getCantileverPageHostId(pageNumber + 1)
    : undefined
  const rightPageRolodexQuery = useMemo(
    () =>
      hasSecondPage
        ? resolvePageImageFileRefDual(
            documentId,
            pageNumber + 1,
            'hiRes',
            rightPageHostId
          )
        : undefined,
    [documentId, hasSecondPage, pageNumber, rightPageHostId]
  )
  const { url: rightPage6400Url, isLoading: isRightPage6400Loading } =
    useRolodexSignedURLs(rightPageRolodexQuery)

  const rolodexOverrideLeft = useMemo<ImageViewerRolodexOverride | undefined>(
    () =>
      leftPage6400Url
        ? { imageUrl: leftPage6400Url, isLoading: false }
        : isLeftPage6400Loading
          ? { imageUrl: undefined, isLoading: true }
          : undefined,
    [leftPage6400Url, isLeftPage6400Loading]
  )

  const rolodexOverrideRight = useMemo<ImageViewerRolodexOverride | undefined>(
    () =>
      rightPage6400Url
        ? { imageUrl: rightPage6400Url, isLoading: false }
        : isRightPage6400Loading
          ? { imageUrl: undefined, isLoading: true }
          : undefined,
    [rightPage6400Url, isRightPage6400Loading]
  )

  const pagesToPreload = useMemo(
    () =>
      rolodexOverrideLeft
        ? computePagesToPreload(pageNumber, totalPages, showMultiplePages)
        : [],
    [rolodexOverrideLeft, pageNumber, totalPages, showMultiplePages]
  )

  const pdfViewerEnabled = isActiveTab

  const {
    imageUrl: imageUrlLeftPage,
    boxes: boxesLeftPage,
    isLoading: isLoadingLeftPage,
  } = useImageViewerLogic(
    pdfUrl || undefined,
    pageNumber,
    scale,
    DocumentViewerPage.LEFT_PAGE,
    totalPages,
    pdfViewerEnabled,
    rolodexOverrideLeft
  )

  const {
    imageUrl: imageUrlRightPage,
    boxes: boxesRightPage,
    isLoading: isLoadingRightPage,
  } = useImageViewerLogic(
    pdfUrl || undefined,
    pageNumber + 1,
    scale,
    DocumentViewerPage.RIGHT_PAGE,
    totalPages,
    pdfViewerEnabled,
    rolodexOverrideRight
  )

  const combinedBoxes = useMemo(
    () =>
      showMultiplePages && totalPages > 1 && hasSecondPage
        ? [...boxesLeftPage, ...boxesRightPage]
        : boxesLeftPage,
    [
      showMultiplePages,
      totalPages,
      hasSecondPage,
      boxesLeftPage,
      boxesRightPage,
    ]
  )

  const combinedImageUrls = useMemo(() => {
    if (
      showMultiplePages &&
      totalPages > 1 &&
      hasSecondPage &&
      imageUrlRightPage &&
      imageUrlLeftPage
    ) {
      return [imageUrlLeftPage, imageUrlRightPage]
    }

    return imageUrlLeftPage ? [imageUrlLeftPage] : undefined
  }, [
    hasSecondPage,
    imageUrlLeftPage,
    imageUrlRightPage,
    showMultiplePages,
    totalPages,
  ])

  const minimapImageUrls = combinedImageUrls ?? []
  const isMinimapZoomed =
    transformState.scale > scaleAtMinZoom.current + 0.001 &&
    !isResettingZoomRef.current
  const isMinimapContentReady =
    imageNaturalDimensions.w > 0 &&
    wrapperDimensions.w > 0 &&
    minimapImageUrls.length > 0
  const {
    showMinimap,
    toggleMinimap,
    resetPreference: resetMinimapPreference,
  } = useMinimapPreference({
    isZoomed: isMinimapZoomed,
    isContentReady: isMinimapContentReady,
  })

  useEffect(() => {
    resetMinimapPreference()
    scaleAtMinZoom.current = 1
    currentScaleRef.current = 1
  }, [documentId, resetMinimapPreference])

  const handleZoomStateChange = useCallback(
    (state: { isAtMinZoom: boolean; isAtMaxZoom: boolean }) => {
      setIsAtMinZoom(state.isAtMinZoom)
      setIsAtMaxZoom(state.isAtMaxZoom)

      if (state.isAtMinZoom) {
        isResettingZoomRef.current = false
        scaleAtMinZoom.current = currentScaleRef.current
        resetMinimapPreference()
      }
    },
    [resetMinimapPreference]
  )

  const updateWrapperDimensions = useCallback(
    (next: { w: number; h: number }) => {
      setWrapperDimensions((prev) => {
        if (prev.w === next.w && prev.h === next.h) return prev
        return next
      })
    },
    []
  )

  const updateImageNaturalDimensions = useCallback(
    (next: { w: number; h: number }) => {
      setImageNaturalDimensions((prev) => {
        if (prev.w === next.w && prev.h === next.h) return prev
        return next
      })
    },
    []
  )

  const handleTransformChange = useCallback((state: TransformState) => {
    currentScaleRef.current = state.scale
    latestTransformStateRef.current = state

    if (transformFrameRef.current !== null) return

    transformFrameRef.current = requestAnimationFrame(() => {
      transformFrameRef.current = null
      setTransformState(latestTransformStateRef.current)
    })
  }, [])

  const handleImageDimensionsChange = useCallback(
    (dimensions: { w: number; h: number }) => {
      updateImageNaturalDimensions(dimensions)
    },
    [updateImageNaturalDimensions]
  )

  const syncViewerMeasurements = useCallback(() => {
    const viewportInfo = viewerRef.current?.getViewportInfo()
    if (viewportInfo) {
      updateWrapperDimensions({
        w: viewportInfo.wrapperWidth,
        h: viewportInfo.wrapperHeight,
      })
    }

    const naturalDimensions = viewerRef.current?.getImageNaturalDimensions()
    if (
      naturalDimensions &&
      naturalDimensions.w > 0 &&
      naturalDimensions.h > 0
    ) {
      updateImageNaturalDimensions(naturalDimensions)
    }
  }, [updateImageNaturalDimensions, updateWrapperDimensions])

  useEffect(() => {
    const element = viewerRootRef.current
    if (!element) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return

      updateWrapperDimensions({
        w: entry.contentRect.width,
        h: entry.contentRect.height,
      })
    })

    observer.observe(element)
    return () => observer.disconnect()
  }, [updateWrapperDimensions])

  useEffect(() => {
    return () => {
      if (transformFrameRef.current !== null) {
        cancelAnimationFrame(transformFrameRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      syncViewerMeasurements()
    })

    return () => cancelAnimationFrame(frameId)
  }, [
    combinedImageUrls,
    pageNumber,
    pdfUrl,
    scale,
    showMultiplePages,
    syncViewerMeasurements,
  ])

  // Resolve the frame target's page number to an index into the displayed
  // image pair; undefined when its page isn't currently displayed (the tab
  // content navigates to the page before the target can apply).
  const viewerFrameTarget = useMemo<
    { bbox: FrameTarget['bbox']; pageIndex: 0 | 1 } | undefined
  >(() => {
    if (!frameTarget) return undefined
    if (frameTarget.pageNumber === pageNumber) {
      return { bbox: frameTarget.bbox, pageIndex: 0 }
    }
    if (hasSecondPage && frameTarget.pageNumber === pageNumber + 1) {
      return { bbox: frameTarget.bbox, pageIndex: 1 }
    }
    return undefined
  }, [frameTarget, pageNumber, hasSecondPage])

  const handleZoomOut = useCallback(() => {
    viewerRef.current?.zoomOut()
  }, [])

  const handleZoomIn = useCallback(() => {
    viewerRef.current?.zoomIn()
  }, [])

  const handleResetZoom = useCallback(() => {
    isResettingZoomRef.current = true
    viewerRef.current?.resetZoom()
  }, [])

  const handleMinimapNavigate = useCallback(
    (positionX: number, positionY: number, animated: boolean) => {
      viewerRef.current?.setTransform(
        positionX,
        positionY,
        transformState.scale,
        animated ? 200 : 0
      )
    },
    [transformState.scale]
  )

  useImperativeHandle(
    ref,
    () => ({
      zoomToBox: (id: string, paddingPx?: number) =>
        viewerRef.current?.zoomToBox(id, paddingPx),
      panToRegion: (bbox) => viewerRef.current?.panToRegion?.(bbox),
      setHighlightedBoxIds: (boxIds: string[]) =>
        viewerRef.current?.setHighlightedBoxIds(boxIds),
      resetZoom: handleResetZoom,
      zoomIn: handleZoomIn,
      zoomOut: handleZoomOut,
      setTransform: (posX, posY, nextScale, animTime = 0) =>
        viewerRef.current?.setTransform(posX, posY, nextScale, animTime),
      getViewportInfo: () => viewerRef.current?.getViewportInfo() ?? null,
      getImageNaturalDimensions: () =>
        viewerRef.current?.getImageNaturalDimensions() ?? { w: 0, h: 0 },
    }),
    [handleResetZoom, handleZoomIn, handleZoomOut]
  )

  if (combinedImageUrls && combinedImageUrls.length > 0) {
    return (
      <DocumentViewerRoot ref={viewerRootRef}>
        <ImageViewerWithOverlay
          ref={viewerRef}
          imageUrls={combinedImageUrls}
          boxes={combinedBoxes}
          searchHighlights={searchHighlights}
          frameTarget={viewerFrameTarget}
          onFrameApplied={onFrameApplied}
          highlightBoxIds={highlightBoxIds}
          onBoxClick={onBoxClick}
          onBoxAuxClick={onBoxAuxClick}
          onBoxContextMenu={onBoxContextMenu}
          onBoxHover={onBoxHover}
          onMultipleBoxSelect={onMultipleBoxSelect}
          onAliasClick={onAliasClick}
          onDocReferenceClick={onDocReferenceClick}
          onDocReferenceHover={onDocReferenceHover}
          actionMode={actionMode}
          showTooltips={showTooltips}
          onTransformChange={handleTransformChange}
          onImageDimensionsChange={handleImageDimensionsChange}
          onZoomStateChange={handleZoomStateChange}
        />
        <ZoomControls
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onResetZoom={handleResetZoom}
          onToggleMinimap={toggleMinimap}
          isMinimapVisible={showMinimap}
          disabled={
            !documentId || !combinedImageUrls || combinedImageUrls.length === 0
          }
          zoomOutDisabled={isAtMinZoom}
          zoomInDisabled={isAtMaxZoom}
        />
        {showMinimap && (
          <DocumentMinimap
            imageUrls={minimapImageUrls}
            transformState={transformState}
            imageNaturalDimensions={imageNaturalDimensions}
            wrapperDimensions={wrapperDimensions}
            onNavigate={handleMinimapNavigate}
            isMultipage={combinedImageUrls.length > 1}
          />
        )}
        {(isLoadingLeftPage || isLoadingRightPage) && (
          <PageLoadingIndicator>
            <PageLoadingSpinner size={16} />
          </PageLoadingIndicator>
        )}
        {pagesToPreload.map((page) => (
          <RolodexPagePreloader
            key={page}
            documentId={documentId}
            page={page}
          />
        ))}
      </DocumentViewerRoot>
    )
  }

  return <LoadingContainer>Loading document...</LoadingContainer>
})
